import type { z, ZodType } from "zod";

import { looseUuid } from "@thalamos/common";

import type { InstrumentDigestUnbranded } from "../assessments/digest.js";

export type InstrumentVersionIdUnbranded = string;
export const InstrumentVersionIdSchema =
  looseUuid().brand<"haven-common:InstrumentVersionId">() satisfies ZodType<InstrumentVersionIdUnbranded>;
/**
 * The stored row of one published instrument version, which an assessment
 * pins. Not what names the version to a caller: that is its digest. Branded
 * apart from `AssessmentId` because an assessment and the version it pins are
 * both UUIDs, written side by side when one starts.
 */
export type InstrumentVersionId = z.infer<typeof InstrumentVersionIdSchema>;

/**
 * A published instrument's schema, as the API serves it.
 *
 * Free-form in the API contract, deliberately: the document *is* a contract,
 * and describing its shape in a second one would be a description of a
 * description that could only drift from it. A caller validates against the
 * document itself.
 */
export type PublishedSchemaDocumentUnbranded = Record<string, unknown>;

/**
 * One published version, as the catalogue names it.
 *
 * Without the schema, which is the larger part of a version and is fetched by
 * digest when a caller wants it. What is here is what picking a version needs:
 * what it is called, what identifies it, and what it followed.
 *
 * `name` and `predecessor` are rendered — `sample/core/1.0/r1` — rather than
 * carried as segments, because that is what a URL holds and what a caller
 * passes back.
 */
export type PublishedInstrumentUnbranded = {
  name: string;
  /** `sha256:…` of the schema's canonical spelling: the version's identity. */
  digest: InstrumentDigestUnbranded;
  /** The version this one followed, or nothing where it is the first of its instrument. */
  predecessor: string | null;
  /**
   * Every key an earlier version asked and this one does not. A key here is
   * dead: no later version of this instrument's family may ask it again.
   */
  retiredItems: string[];
  publishedAt: Date;
};

/** Every published version, oldest first, which is the order they were published in. */
export type PublishedInstrumentsUnbranded = {
  instruments: PublishedInstrumentUnbranded[];
};
